/**
 * Abbonamenti — la panoramica.
 *
 * Quello che si deve capire entrando, in quest'ordine: quanto entra al mese,
 * cosa si rinnova adesso, cosa non torna. Tutto il resto viene dopo.
 *
 * Due scelte da spiegare:
 *  - i canoni che NON passano da Stripe (Villa Fago, New Omnia, Emmequadro,
 *    Graci) stanno nella stessa schermata, in un blocco che dice chiaramente
 *    che si fatturano a mano. Mostrare solo Stripe darebbe una fotografia
 *    parziale che sembra completa, che e' il modo piu' rapido per far prendere
 *    una decisione sbagliata;
 *  - quello che non torna e' scritto in pagina, non nascosto in un log: i due
 *    GoDeal regalati, gli abbonamenti senza anagrafica, i clienti Stripe che in
 *    rubrica non esistono.
 */
import { useNavigate } from 'react-router-dom';
import { Icon } from '../../components/ui/Icon';
import { fmtEur } from '../../lib/utils';
import { MANAGED_SERVICE_LABEL } from '@masamune/shared';
import type { Overview, OverviewRow } from './subscriptions.types';
import './subscriptions.css';

/** Data corta: "2 ott". Le liste di rinnovi si leggono per giorno, non per anno. */
function giorno(d: string | null): string {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('it-IT', { day: 'numeric', month: 'short' });
}

/** Con l'anno, per le scadenze oltre l'orizzonte breve. */
function giornoAnno(d: string | null): string {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('it-IT', { day: 'numeric', month: 'short', year: '2-digit' });
}

/** Il nome da mostrare: l'anagrafica se c'e', altrimenti il nome su Stripe. */
function chi(r: OverviewRow): string {
  return r.clientName ?? r.stripeCustomerName ?? 'Senza anagrafica';
}

function RigaRinnovo({ r, conAnno, onOpen }: { r: OverviewRow; conAnno?: boolean; onOpen: (id: string) => void }) {
  return (
    <div className="sb-row click" role="button" tabIndex={0} onClick={() => onOpen(r.id)}
      onKeyDown={(e) => { if (e.key === 'Enter') onOpen(r.id); }}>
      <span className="d">{conAnno ? giornoAnno(r.currentPeriodEnd) : giorno(r.currentPeriodEnd)}</span>
      <span className="n">
        <span className="t">{chi(r)}</span>
        <span className="s">{r.planLabel} · {MANAGED_SERVICE_LABEL[r.type]}</span>
      </span>
      {/* Zero euro non si scrive "0": si scrive che e' gratis, altrimenti sembra
          un dato mancante invece di una scelta commerciale. */}
      <span className={`a${r.amount === 0 ? ' zero' : ''}`}>
        {r.amount === 0 ? 'gratis' : `€ ${fmtEur(r.amount)}`}
      </span>
    </div>
  );
}

export function SubscriptionsOverview({ data, onVaiElenco }: { data: Overview; onVaiElenco: (filtro: string) => void }) {
  const navigate = useNavigate();
  const apri = (id: string) => navigate(`/services/${id}`);
  const a = data.attention;

  // Il righello dei tipi: la quota di ciascuno sul totale incassato.
  const maxMrr = Math.max(1, ...data.byType.map((t) => t.mrr));

  // Le segnalazioni, in ordine di quanto costano. Si costruisce una lista sola
  // cosi' il blocco sparisce del tutto quando non c'e' niente da dire.
  const segnalazioni: Array<{ key: string; grave?: boolean; testo: React.ReactNode; perche: string; vai?: () => void }> = [];

  if (a.zeroRevenue.length > 0) {
    segnalazioni.push({
      key: 'zero',
      grave: true,
      testo: <><strong>{a.zeroRevenue.length} abbonamenti attivi non incassano niente</strong> — {a.zeroRevenue.map((z) => chi(z)).join(', ')}</>,
      perche: `Il servizio gira ma la fattura lo azzera con una riga negativa. A listino sarebbero € ${fmtEur(a.zeroRevenueYearlyList)} all'anno.`,
      vai: () => onVaiElenco('gratis'),
    });
  }
  if (a.discounted.length > 0) {
    segnalazioni.push({
      key: 'sconti',
      testo: <><strong>{a.discounted.length} abbonamenti scontati in modo permanente</strong></>,
      perche: `Coupon senza scadenza: € ${fmtEur(a.discountYearly)} all'anno di differenza fra listino e incassato.`,
      vai: () => onVaiElenco('scontati'),
    });
  }
  if (a.noClientActive.length > 0) {
    segnalazioni.push({
      key: 'noclient',
      grave: true,
      testo: <><strong>{a.noClientActive.length} abbonamenti attivi senza cliente in rubrica</strong> — {a.noClientActive.map((z) => z.stripeCustomerName ?? 'nome non disponibile').join(', ')}</>,
      perche: 'Incassano, ma non si agganciano a nessuna scheda: non compaiono nella marginalità del cliente né nei suoi documenti.',
      vai: () => onVaiElenco('senza-cliente'),
    });
  }
  if (a.overdue.length > 0) {
    segnalazioni.push({
      key: 'overdue',
      grave: true,
      testo: <><strong>{a.overdue.length} abbonamenti attivi con la data di rinnovo già passata</strong></>,
      perche: 'Vuol dire che il rinnovo non è stato registrato: o Stripe non si sincronizza, o l’addebito non è andato.',
      vai: () => onVaiElenco('scaduti'),
    });
  }
  if (a.pastDue.length > 0) {
    segnalazioni.push({
      key: 'pastdue',
      grave: true,
      testo: <><strong>{a.pastDue.length} pagamenti falliti</strong></>,
      perche: 'Stripe ha provato ad addebitare e non ci è riuscito: vanno sollecitati.',
      vai: () => onVaiElenco('PAST_DUE'),
    });
  }
  if (a.noClientCancelledCount > 0) {
    segnalazioni.push({
      key: 'noclient-closed',
      testo: <><strong>{a.noClientCancelledCount} abbonamenti chiusi senza cliente in rubrica</strong></>,
      perche: 'Sono storia: non incassano più, quindi non urgono. Restano qui per non perdere lo storico.',
    });
  }

  return (
    <div className="ed-section">
      <div className="sb-cols">
        {/* ── Colonna di sinistra: le date ─────────────────────────────── */}
        <div>
          {/* t-micro, non uno <span> nudo: e' cosi' che si scrive l'intestazione
              di una sezione-dato in tutto il gestionale (88 volte su 88). Qui
              erano rimaste sei intestazioni a corpo normale, e la Panoramica
              sembrava impaginata da un'altra parte rispetto alla scheda. */}
          <div className="data-hd">
            <span className="t-micro">Si rinnova nei prossimi {data.renewals.windowDays} giorni</span>
            <span className="mute text-[12px]">
              {data.renewals.next30.count} · € {fmtEur(data.renewals.next30.amount)}
            </span>
          </div>
          {data.renewals.next30.items.length === 0 ? (
            <div className="mute text-[13px] py-3">Niente in scadenza nei prossimi {data.renewals.windowDays} giorni.</div>
          ) : (
            <div>
              {data.renewals.next30.items.map((r) => <RigaRinnovo key={r.id} r={r} onOpen={apri} />)}
            </div>
          )}

          <div className="ed-divider" />

          <div className="data-hd">
            <span className="t-micro">In arrivo, entro {data.renewals.horizonDays} giorni</span>
            <span className="mute text-[12px]">
              {data.renewals.later.count} · € {fmtEur(data.renewals.later.amount)}
            </span>
          </div>
          {data.renewals.later.items.length === 0 ? (
            <div className="mute text-[13px] py-3">Nessun rinnovo nella finestra lunga.</div>
          ) : (
            <div>
              {data.renewals.later.items.map((r) => <RigaRinnovo key={r.id} r={r} conAnno onOpen={apri} />)}
            </div>
          )}
        </div>

        {/* ── Colonna di destra: la composizione e quello che non torna ─── */}
        <div>
          <div className="data-hd"><span className="t-micro">Cosa incassiamo, per tipo</span></div>
          {data.byType.map((t) => (
            <div key={t.type} style={{ padding: '10px 0', borderBottom: '1px solid var(--line)' }}>
              {/* 13px = --fs-small, la scala del testo secondario. Erano 13.5:
                  mezzo pixel piu' delle righe dei rinnovi qui accanto. */}
              <div className="flex items-baseline justify-between gap-3">
                <span className="text-[13px]">{MANAGED_SERVICE_LABEL[t.type]}</span>
                <span className="text-[13px] mono">€ {fmtEur(t.mrr)}</span>
              </div>
              <div className="flex items-baseline justify-between gap-3">
                <span className="mute text-[12px]">
                  {t.active} attivi{t.total > t.active ? ` · ${t.total - t.active} chiusi nel tempo` : ''}
                </span>
                <span className="mute text-[12px]">al mese</span>
              </div>
              <div className="sb-bar"><i style={{ width: `${(t.mrr / maxMrr) * 100}%` }} /></div>
            </div>
          ))}

          {segnalazioni.length > 0 && (
            <>
              <div className="ed-divider" />
              <div className="data-hd"><span className="t-micro">Quello che non torna</span></div>
              <div className="sb-flags">
                {segnalazioni.map((s) => (
                  <div key={s.key} className={`sb-flag${s.grave ? ' warn' : ''}`}>
                    <span className="ic"><Icon name={s.grave ? 'alert' : 'help'} size={12} /></span>
                    <span className="tx">
                      {s.testo}
                      <span className="why" style={{ display: 'block' }}>{s.perche}</span>
                    </span>
                    {s.vai && <button className="go" onClick={s.vai}>Vedi</button>}
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
